import { useState } from 'react';
import ProcurementNav from '../components/ProcurementNav';
import StatCard from '../components/StatCard';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { inventoryApi } from '../services/api';
import {
  ShoppingCart,
  Package,
  Truck,
  Plus,
  Check,
  Star,
  Trash2,
  Edit2,
  X,
  Send,
  Repeat,
} from 'lucide-react';
import HelpButton from '../components/HelpButton';
import clsx from 'clsx';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SupplierOption {
  vendorItemId: string | null;
  vendorId: string | null;
  vendorName: string;
  orderUnit: string;
  packSize: number;
  unitPriceCents: number;
  pricePerBaseUnitCents: number;
  leadTimeDays: number;
  minimumOrder: number;
  isPreferred: boolean;
  orderUnitsNeeded: number;
  totalCostCents: number;
}

interface OrderSuggestion {
  inventoryItemId: string;
  name: string;
  category: string;
  unit: string;
  currentQuantity: number;
  parLevel: number;
  reorderPoint: number;
  suggestedQuantity: number;
  predictedDailyUsage: number;
  daysOfStockRemaining: number | null;
  urgency: 'critical' | 'high' | 'medium';
  suppliers: SupplierOption[];
  recommendedVendorId: string | null;
  recommendedReason: string | null;
  estimatedCostCents: number;
}

interface OrderSuggestionsResponse {
  suggestions: OrderSuggestion[];
  summary: { total: number; critical: number; estimatedTotalCents: number };
}

interface PurchaseOrderItem {
  id: string;
  inventoryItemId: string;
  quantity: number;
  unitCostCents: number;
  receivedQuantity: number;
}

interface PurchaseOrder {
  id: string;
  vendorId?: string | null;
  poNumber: string;
  status: string;
  totalCents: number;
  expectedDelivery: string | null;
  sentAt?: string | null;
  createdAt: string;
  vendor: { name: string; email?: string | null } | null;
  items: PurchaseOrderItem[];
}

interface RecurringOrder {
  id: string;
  vendorId: string;
  items: { itemId: string; quantity: number; unitCost: number }[];
  frequencyDays: number;
  nextDate: string;
  autoSend: boolean;
  active: boolean;
}

interface Vendor {
  id: string;
  name: string;
  contactName?: string;
  email?: string;
  phone?: string;
  paymentTerms?: string;
}

interface CatalogItem {
  id: string;
  vendorItemName: string;
  vendorSku: string | null;
  unitPriceCents: number;
  orderUnit: string;
  packSize: number;
  packUnit: string | null;
  minimumOrder: number;
  leadTimeDays: number;
  isPreferred: boolean;
  isActive: boolean;
  inventoryItemId: string | null;
  inventoryItem?: { id: string; name: string };
}

interface InventoryItem {
  id: string;
  name: string;
  unit: string;
  unitOfMeasure?: string;
  category?: string;
  currentQuantity: number;
  parLevel: number;
}

// Local UI state for an editable suggestion row
interface RowState {
  selected: boolean;
  quantity: number;
  vendorId: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatCurrency = (cents: number) => `$${((cents || 0) / 100).toFixed(2)}`;

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
};

type Tab = 'to-order' | 'purchase-orders' | 'suppliers';

const TABS: { id: Tab; label: string; icon: typeof ShoppingCart }[] = [
  { id: 'to-order', label: 'To Order', icon: ShoppingCart },
  { id: 'purchase-orders', label: 'Purchase Orders', icon: Package },
  { id: 'suppliers', label: 'Suppliers', icon: Truck },
];

const emptyVendorForm = {
  name: '',
  contactName: '',
  email: '',
  phone: '',
  address: '',
  paymentTerms: '',
};

const emptyCatalogForm = {
  inventoryItemId: '',
  vendorSku: '',
  vendorItemName: '',
  unitPrice: 0,
  orderUnit: 'case',
  packSize: 1,
  packUnit: '',
  minimumOrder: 1,
  leadTimeDays: 1,
  isPreferred: false,
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function OrderingPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('to-order');

  // Editable per-row state for the suggestions tab
  const [rowState, setRowState] = useState<Record<string, RowState>>({});

  // Suppliers tab state
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [vendorForm, setVendorForm] = useState(emptyVendorForm);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [editingCatalog, setEditingCatalog] = useState<CatalogItem | null>(null);
  const [catalogForm, setCatalogForm] = useState(emptyCatalogForm);
  const [formError, setFormError] = useState('');
  const [sendBanner, setSendBanner] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [repeatPO, setRepeatPO] = useState<PurchaseOrder | null>(null);
  const [repeatDays, setRepeatDays] = useState(7);

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  const { data: suggestionsData, isLoading: loadingSuggestions } = useQuery({
    queryKey: ['order-suggestions'],
    queryFn: async () => {
      const response = await inventoryApi.getOrderSuggestions();
      const result = (response.data?.data || response.data) as OrderSuggestionsResponse;
      return result || { suggestions: [], summary: { total: 0, critical: 0, estimatedTotalCents: 0 } };
    },
  });

  const { data: purchaseOrdersData, isLoading: loadingPOs } = useQuery({
    queryKey: ['purchase-orders'],
    queryFn: async () => {
      const response = await inventoryApi.getPurchaseOrders();
      const result = response.data?.data || response.data;
      return (Array.isArray(result) ? result : []) as PurchaseOrder[];
    },
  });

  const { data: vendorsData, isLoading: loadingVendors } = useQuery({
    queryKey: ['vendors'],
    queryFn: async () => {
      const response = await inventoryApi.getVendors();
      const result = response.data?.data || response.data;
      return (Array.isArray(result) ? result : []) as Vendor[];
    },
  });

  const { data: catalogData, isLoading: loadingCatalog } = useQuery({
    queryKey: ['vendor-catalog', selectedVendorId],
    queryFn: async () => {
      if (!selectedVendorId) return [] as CatalogItem[];
      const response = await inventoryApi.getVendorCatalog(selectedVendorId);
      const result = response.data?.data || response.data;
      return (Array.isArray(result) ? result : []) as CatalogItem[];
    },
    enabled: !!selectedVendorId,
  });

  const { data: recurringData } = useQuery({
    queryKey: ['recurring-orders'],
    queryFn: async () => {
      const response = await inventoryApi.getRecurringOrders();
      const result = response.data?.data || response.data;
      return (Array.isArray(result) ? result : []) as RecurringOrder[];
    },
  });

  const { data: itemsData } = useQuery({
    queryKey: ['inventory'],
    queryFn: async () => {
      const response = await inventoryApi.getItems();
      const result = response.data?.data || response.data;
      return (Array.isArray(result) ? result : []) as InventoryItem[];
    },
  });

  const suggestions = suggestionsData?.suggestions || [];
  const summary = suggestionsData?.summary || { total: 0, critical: 0, estimatedTotalCents: 0 };
  const orderWindow = (suggestionsData?.summary as any)?.orderWindow as string | null;
  const { data: schedule } = useQuery({
    queryKey: ['order-schedule-edit'],
    queryFn: async () => (await api.get('/v1/inventory/order-schedule')).data?.data || {},
    staleTime: 60_000,
  });
  const [editSched, setEditSched] = useState<Array<{ day: number; covers: number }> | null>(null);
  const saveSched = useMutation({
    mutationFn: (sched: Array<{ day: number; covers: number }>) => api.put('/v1/inventory/order-schedule', { schedule: sched }),
    onSuccess: () => { setEditSched(null); queryClient.invalidateQueries({ queryKey: ['inventory-suggestions'] }); queryClient.invalidateQueries({ queryKey: ['order-schedule-edit'] }); },
  });
  void schedule;
  const { data: catering } = useQuery({
    queryKey: ['catering-outlook'],
    queryFn: async () => (await api.get('/v1/inventory/catering-outlook')).data?.data,
  });
  const [addCatering, setAddCatering] = useState(false);
  const [catCustomer, setCatCustomer] = useState('');
  const [catDate, setCatDate] = useState('');
  const [catDozens, setCatDozens] = useState<Record<string, number>>({ Everything: 0, Sesame: 0, Plain: 0, Assorted: 0 });
  const [catExtra, setCatExtra] = useState('');
  const createCatering = useMutation({
    mutationFn: async () => {
      const lines: any[] = [];
      for (const [t, dz] of Object.entries(catDozens)) {
        if (dz > 0) lines.push({ sourceName: t === 'Assorted' ? 'Assorted Dozen' : `${t} Bagel`, quantity: t === 'Assorted' ? dz : dz * 12 });
      }
      for (const row of catExtra.split('\n')) {
        const m = row.trim().match(/^(\d+)\s*[x×]?\s+(.+)$/);
        if (m) lines.push({ sourceName: m[2].trim(), quantity: Number(m[1]) });
      }
      return api.post('/v1/production/orders', {
        source: 'catering', customerName: catCustomer.trim(), destination: `Catering — ${catCustomer.trim()}`,
        fulfillmentDate: catDate, lines,
      });
    },
    onSuccess: () => {
      setAddCatering(false); setCatCustomer(''); setCatDate(''); setCatExtra('');
      setCatDozens({ Everything: 0, Sesame: 0, Plain: 0, Assorted: 0 });
      queryClient.invalidateQueries({ queryKey: ['catering-outlook'] });
    },
  });
  const [cateringRemoveId, setCateringRemoveId] = useState<string | null>(null);
  const deleteCatering = useMutation({
    mutationFn: async (id: string) => api.delete(`/v1/production/orders/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['catering-outlook'] }),
  });
  const catReady = catCustomer.trim().length > 0 && catDate.length > 0 &&
    (Object.values(catDozens).some((v) => v > 0) || /\d/.test(catExtra));
  const { data: weather } = useQuery({
    queryKey: ['weather-outlook'],
    queryFn: async () => (await api.get('/v1/inventory/weather-outlook')).data?.data,
    staleTime: 30 * 60_000,
  });
  const { data: bagelPlan } = useQuery({
    queryKey: ['bagel-plan-order'],
    queryFn: async () => (await api.get('/v1/inventory/bagel-plan')).data?.data,
  });
  const [bagelCopied, setBagelCopied] = useState(false);
  const [rainTrim, setRainTrim] = useState(false);
  const { data: vendorMins } = useQuery({
    queryKey: ['vendor-minimums'],
    queryFn: async () => (await api.get('/v1/inventory/vendor-minimums')).data?.data?.minimums || {},
  });
  const setVendorMin = useMutation({
    mutationFn: async (v: { vendorId: string; minimumCents: number | null }) => api.put('/v1/inventory/vendor-minimums', v),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vendor-minimums'] }),
  });
  const { data: budget } = useQuery({
    queryKey: ['declining-budget'],
    queryFn: async () => (await api.get('/v1/inventory/declining-budget')).data?.data,
  });
  const { data: autoOrdering } = useQuery({
    queryKey: ['auto-ordering'],
    queryFn: async () => (await api.get('/v1/inventory/auto-ordering')).data?.data || { mode: 'off' },
  });
  const setAutoMode = useMutation({
    mutationFn: (mode: string) => api.put('/v1/inventory/auto-ordering', { mode }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['auto-ordering'] }),
  });
  const purchaseOrders = purchaseOrdersData || [];
  const vendors = vendorsData || [];
  const catalog = catalogData || [];
  const items = itemsData || [];
  const recurringOrders = recurringData || [];

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const createOrdersMutation = useMutation({
    mutationFn: async (lines: { itemId: string; quantity: number; vendorId?: string; unitCost?: number }[]) => {
      const res = await inventoryApi.createOrdersFromSuggestions(lines);
      return (res.data?.data || res.data) as { ordersCreated: number; skipped?: string[] };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['order-suggestions'] });
      setRowState({});
      const created = result?.ordersCreated || 0;
      const skipped = result?.skipped?.length || 0;
      if (created > 0) {
        setActiveTab('purchase-orders');
        setSendBanner({
          tone: 'success',
          text: `${created} purchase order${created > 1 ? 's' : ''} drafted — press "Send to supplier" on each to email it.${skipped > 0 ? ` ${skipped} item(s) were skipped because they have no supplier.` : ''}`,
        });
      } else {
        setSendBanner({ tone: 'error', text: `No purchase orders created — ${skipped} selected item${skipped === 1 ? ' has' : 's have'} no supplier. Pick one in the dropdown (or add it under Suppliers) and try again.` });
      }
    },
    onError: () => setSendBanner({ tone: 'error', text: 'Could not create the purchase orders — try again in a moment.' }),
  });

  const sendPOMutation = useMutation({
    mutationFn: async (orderId: string) => {
      const res = await inventoryApi.sendPurchaseOrder(orderId);
      return (res.data?.data || res.data) as { sentTo: string };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      setSendBanner({ tone: 'success', text: `Order emailed to ${result.sentTo}. The supplier can reply directly to confirm.` });
    },
    onError: (err: any) => {
      const message = err?.response?.data?.error?.message
        || "The order couldn't be emailed just now — nothing was sent. Try again in a moment.";
      setSendBanner({ tone: 'error', text: message });
    },
  });

  const createRecurrenceMutation = useMutation({
    mutationFn: async (po: PurchaseOrder) => {
      const next = new Date(Date.now() + repeatDays * 86400_000);
      return inventoryApi.createRecurringOrder({
        vendorId: po.vendorId as string,
        items: (po.items || []).map((it) => ({
          itemId: it.inventoryItemId,
          quantity: Number(it.quantity),
          unitCost: it.unitCostCents / 100,
        })),
        frequencyDays: repeatDays,
        nextDate: next.toISOString(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-orders'] });
      setRepeatPO(null);
      setSendBanner({ tone: 'success', text: 'This order now repeats on its own — a fresh draft will appear here each time, ready to send.' });
    },
    onError: () => setSendBanner({ tone: 'error', text: "Couldn't set up the repeat just now — nothing was saved. Try again in a moment." }),
  });

  const updateRecurrenceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { active?: boolean } }) => inventoryApi.updateRecurringOrder(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['recurring-orders'] }),
  });

  const deleteRecurrenceMutation = useMutation({
    mutationFn: (id: string) => inventoryApi.deleteRecurringOrder(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['recurring-orders'] }),
  });

  const createVendorMutation = useMutation({
    mutationFn: (data: typeof emptyVendorForm) =>
      inventoryApi.createVendor({
        name: data.name,
        contactName: data.contactName || undefined,
        email: data.email || undefined,
        phone: data.phone || undefined,
        address: data.address || undefined,
        paymentTerms: data.paymentTerms || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      setShowVendorModal(false);
      setVendorForm(emptyVendorForm);
      setFormError('');
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to create vendor');
    },
  });

  const saveCatalogMutation = useMutation({
    mutationFn: (data: typeof emptyCatalogForm) => {
      const payload = {
        inventoryItemId: data.inventoryItemId || undefined,
        vendorSku: data.vendorSku || undefined,
        vendorItemName: data.vendorItemName,
        unitPriceCents: Math.round((data.unitPrice || 0) * 100),
        orderUnit: data.orderUnit,
        packSize: data.packSize,
        packUnit: data.packUnit || undefined,
        minimumOrder: data.minimumOrder,
        leadTimeDays: data.leadTimeDays,
        isPreferred: data.isPreferred,
      };
      if (editingCatalog) {
        return inventoryApi.updateCatalogItem(editingCatalog.id, payload);
      }
      return inventoryApi.addCatalogItem(selectedVendorId as string, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-catalog', selectedVendorId] });
      setShowCatalogModal(false);
      setEditingCatalog(null);
      setCatalogForm(emptyCatalogForm);
      setFormError('');
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to save catalog item');
    },
  });

  const deleteCatalogMutation = useMutation({
    mutationFn: (catalogId: string) => inventoryApi.deleteCatalogItem(catalogId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-catalog', selectedVendorId] });
    },
  });

  // -------------------------------------------------------------------------
  // Row state helpers (To Order tab)
  // -------------------------------------------------------------------------

  const getRow = (s: OrderSuggestion): RowState => {
    return (
      rowState[s.inventoryItemId] || {
        selected: false,
        quantity: s.suggestedQuantity,
        vendorId: s.recommendedVendorId,
      }
    );
  };

  const updateRow = (id: string, base: OrderSuggestion, patch: Partial<RowState>) => {
    setRowState((prev) => {
      const current =
        prev[id] || (() => {
          const rec = base.suppliers.find((x) => x.vendorId === base.recommendedVendorId) || base.suppliers[0];
          const pack = rec && Number(rec.packSize) > 1 ? Number(rec.packSize) : 1;
          const qty = pack > 1 ? Math.ceil(base.suggestedQuantity / pack - 1e-9) * pack : base.suggestedQuantity;
          return { selected: false, quantity: Math.round(qty * 100) / 100, vendorId: base.recommendedVendorId };
        })();
      return { ...prev, [id]: { ...current, ...patch } };
    });
  };

  const getChosenSupplier = (s: OrderSuggestion): SupplierOption | undefined => {
    const row = getRow(s);
    return (
      s.suppliers.find((sup) => sup.vendorId === row.vendorId) ||
      s.suppliers.find((sup) => sup.isPreferred) ||
      s.suppliers[0]
    );
  };

  const selectedRows = suggestions.filter((s) => getRow(s).selected);

  const handleCreateOrders = () => {
    const lines = selectedRows
      .map((s) => {
        const row = getRow(s);
        const supplier = getChosenSupplier(s);
        return {
          itemId: s.inventoryItemId,
          quantity: row.quantity,
          // Fall back to a manually picked vendor when the item has no catalog supplier
          vendorId: supplier?.vendorId || row.vendorId || undefined,
          unitCost: supplier ? supplier.pricePerBaseUnitCents / 100 : undefined,
        };
      })
      .filter((line) => line.quantity > 0);
    if (lines.length === 0) return;
    createOrdersMutation.mutate(lines);
  };

  // -------------------------------------------------------------------------
  // Suppliers tab helpers
  // -------------------------------------------------------------------------

  const openAddCatalog = () => {
    setEditingCatalog(null);
    setCatalogForm(emptyCatalogForm);
    setFormError('');
    setShowCatalogModal(true);
  };

  const openEditCatalog = (entry: CatalogItem) => {
    setEditingCatalog(entry);
    setCatalogForm({
      inventoryItemId: entry.inventoryItemId || '',
      vendorSku: entry.vendorSku || '',
      vendorItemName: entry.vendorItemName,
      unitPrice: entry.unitPriceCents / 100,
      orderUnit: entry.orderUnit,
      packSize: entry.packSize,
      packUnit: entry.packUnit || '',
      minimumOrder: entry.minimumOrder,
      leadTimeDays: entry.leadTimeDays,
      isPreferred: entry.isPreferred,
    });
    setFormError('');
    setShowCatalogModal(true);
  };

  const urgencyBadge = (urgency: OrderSuggestion['urgency']) =>
    clsx(
      'badge text-xs',
      urgency === 'critical' && 'badge-danger',
      urgency === 'high' && 'bg-orange-100 text-orange-700',
      urgency === 'medium' && 'bg-slate-100 text-slate-600'
    );

  const statusBadge = (status: string) =>
    clsx(
      'badge text-xs capitalize',
      status === 'received' && 'badge-success',
      (status === 'draft' || status === 'pending') && 'bg-slate-100 text-slate-600',
      (status === 'ordered' || status === 'submitted') && 'bg-primary-100 text-primary-700',
      status === 'cancelled' && 'badge-danger'
    );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 pb-safe">
      <ProcurementNav />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Ordering</h1>
            <p className="text-sm md:text-base text-slate-500">
              Reorder stock, manage purchase orders and suppliers
            </p>
          </div>
          <HelpButton featureId="ordering" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 overflow-x-auto scrollbar-hide">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={clsx(
              'flex items-center gap-2 px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors',
              activeTab === id
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ===================================================================
          TAB: To Order
      =================================================================== */}
      {activeTab === 'to-order' && (
        <div className="space-y-4 md:space-y-6">
          <div className={`card p-4 ${(catering?.flags?.length || 0) > 0 ? 'bg-amber-50/60 border-amber-200' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wide text-slate-400">Catering coming up</p>
              {!addCatering && (
                <button className="text-xs text-primary-600 hover:text-primary-700 font-medium" onClick={() => setAddCatering(true)}>
                  + Add catering order
                </button>
              )}
            </div>
            {(catering?.orders?.length || 0) > 0 ? (
              <div className="space-y-1">
                {catering.orders.map((o: any) => (
                  <div key={o.id} className="flex items-start justify-between gap-2">
                    <p className="text-sm text-slate-700">
                      <span className="font-medium">{o.customer}</span>
                      <span className="text-slate-400"> · {new Date(o.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}</span>
                      <span className="text-slate-500"> · {o.lines.map((l: any) => `${l.qty}× ${l.name}`).join(', ')}{o.bagels ? ` (${o.bagels} bagels)` : ''}</span>
                    </p>
                    {cateringRemoveId === o.id ? (
                      <span className="flex items-center gap-1.5 shrink-0 text-xs">
                        <button className="text-red-600 font-medium" onClick={() => { deleteCatering.mutate(o.id); setCateringRemoveId(null); }}>Remove</button>
                        <button className="text-slate-400" onClick={() => setCateringRemoveId(null)}>Keep</button>
                      </span>
                    ) : (
                      <button className="text-slate-300 hover:text-red-500 shrink-0" title="Remove this catering order"
                        onClick={() => setCateringRemoveId(o.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : !addCatering ? (
              <p className="text-sm text-slate-400">No catering on the books. Add one and ordering will plan ingredients and boxes around it.</p>
            ) : null}
            {(catering?.flags?.length || 0) > 0 ? (
              <div className="mt-2 space-y-1">
                {catering.flags.map((f: any) => (
                  <p key={f.itemId} className="text-sm text-amber-800">
                    ⚠️ Short <span className="font-semibold">{f.shortQty} {f.unit} {f.name}</span> for these orders (need {f.neededQty}, have {f.haveQty} incl. on-order) — add it to this order.
                  </p>
                ))}
              </div>
            ) : (catering?.orders?.length || 0) > 0 ? (
              <p className="text-xs text-emerald-700 mt-2">Stock + open orders cover everything, packaging included.</p>
            ) : null}
            {addCatering && (
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input className="input text-sm" placeholder="Who is it for? e.g. Hudson & Co." value={catCustomer} onChange={(e) => setCatCustomer(e.target.value)} />
                  <input className="input text-sm" type="date" value={catDate} onChange={(e) => setCatDate(e.target.value)} />
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">Bagels, in dozens</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(catDozens).map((t) => (
                      <div key={t} className="flex items-center gap-1.5 border border-slate-200 rounded-lg px-2 py-1">
                        <span className="text-sm text-slate-600">{t}</span>
                        <button className="w-6 h-6 rounded text-slate-500 hover:bg-slate-100" onClick={() => setCatDozens((d) => ({ ...d, [t]: Math.max(0, d[t] - 1) }))}>−</button>
                        <span className="text-sm font-medium w-5 text-center">{catDozens[t]}</span>
                        <button className="w-6 h-6 rounded text-slate-500 hover:bg-slate-100" onClick={() => setCatDozens((d) => ({ ...d, [t]: d[t] + 1 }))}>+</button>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">Anything else — one per line, like "4 Cream Cheese 1/2 lb Tub"</p>
                  <textarea className="input text-sm w-full" rows={2} value={catExtra} onChange={(e) => setCatExtra(e.target.value)} placeholder="2 Lox (3oz)" />
                </div>
                <div className="flex items-center gap-2">
                  <button className="btn-primary text-sm px-3 py-1.5 disabled:opacity-50" disabled={!catReady || createCatering.isPending}
                    onClick={() => createCatering.mutate()}>
                    {createCatering.isPending ? 'Adding…' : 'Add order'}
                  </button>
                  <button className="text-sm text-slate-500 hover:text-slate-700" onClick={() => setAddCatering(false)}>Cancel</button>
                  <span className="text-xs text-slate-400">Ingredients and catering boxes get planned into ordering automatically.</span>
                </div>
              </div>
            )}
          </div>
          {weather?.advisory && (
            <div className="card px-5 py-3 bg-sky-50/50 border-sky-100">
              <p className="text-sm text-sky-900">☔ {weather.advisory}</p>
            </div>
          )}
          {bagelPlan?.suggested && (bagelPlan.suggested.everything + bagelPlan.suggested.sesame + bagelPlan.suggested.plain + (bagelPlan.suggested.unsplit || 0)) > 0 && (() => {
            const wetDay = (weather?.days || []).find((d: any) => d.date === bagelPlan.date && d.precipProb >= 60);
            const round5 = (x: number) => Math.max(0, Math.ceil(x / 5) * 5);
            const adj = (v: number) => (rainTrim && wetDay ? round5(v * 0.9) : v);
            const sug = {
              everything: adj(bagelPlan.suggested.everything), sesame: adj(bagelPlan.suggested.sesame),
              plain: adj(bagelPlan.suggested.plain), unsplit: adj(bagelPlan.suggested.unsplit || 0),
              assortedDozens: bagelPlan.suggested.assortedDozens,
            };
            return (
            <div className="card p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Tomorrow's bagels · bakery order</p>
                  <p className="text-sm text-slate-700 tabular-nums">
                    {[['everything', sug.everything], ['sesame', sug.sesame], ['plain', sug.plain]]
                      .filter(([, v]) => (v as number) > 0)
                      .map(([k, v], i) => <span key={k as string}>{i > 0 && ' · '}<span className="font-semibold">{v as number}</span> {k as string}</span>)}
                    {(sug.unsplit || 0) > 0 && <> {(sug.everything + sug.sesame + sug.plain) > 0 && '· '}<span className="font-semibold">{sug.unsplit}</span> bagels (sold untyped — splits by type once the POS carries it)</>}
                    {sug.assortedDozens > 0 && <> · <span className="font-semibold">{sug.assortedDozens}</span> assorted dozens</>}
                  </p>
                  {wetDay && (
                    <label className="flex items-center gap-1.5 text-xs text-sky-800 mt-1 cursor-pointer">
                      <input type="checkbox" checked={rainTrim} onChange={(e) => setRainTrim(e.target.checked)} />
                      {wetDay.label} that day ({wetDay.precipProb}%) — trim the bake 10%{rainTrim ? ' · trimmed' : ''}
                    </label>
                  )}
                  <p className="text-xs text-slate-400 mt-0.5">
                    Your last {bagelPlan.basedOnWeeks} {new Date(bagelPlan.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' })}s{(bagelPlan.catering?.everything + bagelPlan.catering?.sesame + bagelPlan.catering?.plain + bagelPlan.catering?.assortedDozens) > 0 ? ', plus catering due that day' : ''}, rounded up to 5s.
                  </p>
                </div>
                <button className="btn btn-sm btn-secondary"
                  onClick={() => {
                    const d = new Date(bagelPlan.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    const lines = [['Everything', sug.everything], ['Sesame', sug.sesame], ['Plain', sug.plain], ['Bagels (assorted)', sug.unsplit || 0], ['Assorted dozens', sug.assortedDozens]].filter(([, v]) => (v as number) > 0);
                    const t = `Bagel order for ${d}:\n${lines.map(([k, v]) => `• ${k}: ${v}`).join('\n')}${rainTrim && wetDay ? `\n(trimmed 10% for the ${wetDay.label.toLowerCase()} forecast)` : ''}\n\nThank you!`;
                    navigator.clipboard.writeText(t).then(() => { setBagelCopied(true); setTimeout(() => setBagelCopied(false), 2500); });
                  }}>
                  {bagelCopied ? 'Copied ✓' : 'Copy order for the bakery'}
                </button>
              </div>
            </div>
            );
          })()}
          {budget?.configured && (
            <div className="card p-4">
              <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
                <p className="text-xs uppercase tracking-wide text-slate-400">Declining budget · this week</p>
                <p className="text-sm tabular-nums">
                  <span className="font-semibold text-slate-900">${Math.round(budget.remainingCents / 100).toLocaleString('en-US')} left</span>
                  <span className="text-slate-400"> of ${Math.round(budget.budgetCents / 100).toLocaleString('en-US')}</span>
                  <span className={`ml-2 rounded px-1.5 py-0.5 text-xs font-medium ${budget.status === 'on_pace' ? 'bg-emerald-50 text-emerald-700' : budget.status === 'warm' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                    {budget.status === 'on_pace' ? 'on pace' : budget.status === 'warm' ? 'running warm' : 'over pace'}
                  </span>
                </p>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden relative">
                <div className={`h-2 ${budget.status === 'hot' ? 'bg-red-400' : 'bg-primary-500'}`} style={{ width: `${Math.min(100, budget.pacePct)}%` }} />
                <div className="absolute top-0 h-2 w-0.5 bg-slate-500" style={{ left: `${budget.weekElapsedPct}%` }} title="where the week is" />
              </div>
              <p className="text-xs text-slate-400 mt-1.5">
                Spent ${Math.round(budget.spentCents / 100).toLocaleString('en-US')} in {budget.daysIn} day{budget.daysIn === 1 ? '' : 's'} · budget = expected sales ${Math.round(budget.expectedWeekSalesCents / 100).toLocaleString('en-US')} × {budget.cogsTargetPct}% COGS target · tick = where the week is
              </p>
            </div>
          )}
          {orderWindow && (
            <div className="card px-5 py-3 bg-primary-50/60 border-primary-100 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-primary-900">{orderWindow} — suggestions target that window's usage.
                <button className="ml-2 text-xs underline text-primary-700" onClick={() => setEditSched((schedule?.schedule?.length ? schedule.schedule : [{ day: 0, covers: 4 }, { day: 4, covers: 3 }]))}>edit days</button>
              </p>
              {editSched && (
                <div className="w-full mt-2 space-y-1.5">
                  {editSched.map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <select className="input input-sm w-32" value={row.day}
                        onChange={(e) => setEditSched(editSched.map((x, j) => j === i ? { ...x, day: +e.target.value } : x))}>
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((d, di) => <option key={di} value={di}>{d}</option>)}
                      </select>
                      <span className="text-xs text-slate-500">covers</span>
                      <input type="number" min={1} max={14} className="input input-sm w-16 text-right" value={row.covers}
                        onChange={(e) => setEditSched(editSched.map((x, j) => j === i ? { ...x, covers: +e.target.value || 1 } : x))} />
                      <span className="text-xs text-slate-500">days</span>
                      <button className="text-slate-300 hover:text-red-500 text-sm" onClick={() => setEditSched(editSched.filter((_, j) => j !== i))}>✕</button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <button className="btn btn-sm btn-ghost" onClick={() => setEditSched([...editSched, { day: 1, covers: 2 }])}>+ order day</button>
                    <button className="btn btn-sm btn-primary" disabled={!editSched.length || saveSched.isPending} onClick={() => saveSched.mutate(editSched)}>
                      {saveSched.isPending ? 'Saving…' : 'Save schedule'}
                    </button>
                    <button className="btn btn-sm btn-ghost" onClick={() => setEditSched(null)}>Cancel</button>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-xs text-primary-900/70">Order day runs itself:</span>
                <select
                  className="input input-sm w-44"
                  value={autoOrdering?.mode || 'off'}
                  onChange={(e) => setAutoMode.mutate(e.target.value)}
                >
                  <option value="off">Off — order by hand</option>
                  <option value="draft">Draft for approval</option>
                  <option value="auto">Fully automatic</option>
                </select>
                {autoOrdering?.mode === 'draft' && (
                  <select
                    className="input input-sm w-52"
                    value={autoOrdering?.sendUnapprovedAtHour || 0}
                    onChange={(e) => api.put('/v1/inventory/auto-ordering', { mode: 'draft', sendUnapprovedAtHour: +e.target.value })
                      .then(() => queryClient.invalidateQueries({ queryKey: ['auto-ordering'] }))}
                    title="If nobody approves by then, the drafted order sends itself"
                  >
                    <option value={0}>Wait for approval, always</option>
                    {[9, 10, 11, 12, 14].map((h) => (
                      <option key={h} value={h}>Unapproved by {h > 12 ? h - 12 + 'pm' : h + 'am'}? Send anyway</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          )}
          {/* Supplier minimums — does today's suggestion make each truck? */}
          {(() => {
            const sugg = suggestionsData?.suggestions || [];
            const byVendor = new Map<string, { name: string; cents: number }>();
            for (const g of sugg) {
              const vid = g.recommendedVendorId; if (!vid) continue;
              const sup = (g.suppliers || []).find((x) => x.vendorId === vid);
              const cur = byVendor.get(vid) || { name: sup?.vendorName || 'Supplier', cents: 0 };
              cur.cents += g.estimatedCostCents || 0;
              byVendor.set(vid, cur);
            }
            const rows = [...byVendor.entries()].map(([vid, v]) => ({ vid, ...v, min: (vendorMins || {})[vid] || 0 }));
            if (!rows.length) return null;
            return (
              <div className="flex flex-wrap gap-2 items-center">
                {rows.map((r) => (
                  <button key={r.vid}
                    className={`text-xs px-2.5 py-1 rounded-full border ${r.min > 0 && r.cents < r.min ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-slate-50 border-slate-200 text-slate-500'}`}
                    title="Click to set this supplier's order minimum"
                    onClick={() => {
                      const v = window.prompt(`Order minimum for ${r.name}, in dollars (0 to clear):`, r.min ? String(r.min / 100) : '');
                      if (v !== null) setVendorMin.mutate({ vendorId: r.vid, minimumCents: Math.round((parseFloat(v) || 0) * 100) || null });
                    }}>
                    {r.name}: {formatCurrency(r.cents)}
                    {r.min > 0 && r.cents < r.min ? ` — ${formatCurrency(r.min - r.cents)} short of the ${formatCurrency(r.min)} minimum` : r.min > 0 ? ' ✓ makes the truck' : ''}
                  </button>
                ))}
                <span className="text-[11px] text-slate-400">tap a supplier to set its minimum</span>
              </div>
            );
          })()}
          {/* Summary cards — kit pattern */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            <StatCard title="Items to order" value={summary.total} icon={<ShoppingCart className="w-4 h-4 md:w-5 md:h-5 text-primary-600" />} iconBg="bg-primary-50" />
            <StatCard title="Critical" value={summary.critical} sub={summary.critical > 0 ? 'running out first' : undefined} subTone="danger" icon={<Package className="w-4 h-4 md:w-5 md:h-5 text-danger-600" />} iconBg="bg-danger-50" />
            <StatCard title="Estimated total" value={formatCurrency(summary.estimatedTotalCents)} icon={<Send className="w-4 h-4 md:w-5 md:h-5 text-success-600" />} iconBg="bg-success-50" />
          </div>
          {/* Action bar */}
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-slate-500">
              {selectedRows.length} selected
            </p>
            <button
              onClick={handleCreateOrders}
              disabled={selectedRows.length === 0 || createOrdersMutation.isPending}
              className="btn btn-primary flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              {createOrdersMutation.isPending ? 'Creating...' : 'Create Purchase Orders'}
            </button>
          </div>

          {loadingSuggestions ? (
            <div className="text-center py-12">Loading...</div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nothing to order right now</p>
              <p className="text-sm">Stock levels are above their reorder points</p>
            </div>
          ) : (
            <div className="space-y-3">
              {suggestions.map((s) => {
                const row = getRow(s);
                const chosen = getChosenSupplier(s);
                return (
                  <div
                    key={s.inventoryItemId}
                    className={clsx(
                      'card p-4 transition-colors',
                      row.selected && 'ring-2 ring-primary-400'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={row.selected}
                        onChange={(e) => updateRow(s.inventoryItemId, s, { selected: e.target.checked })}
                        className="mt-1 w-5 h-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className="font-semibold text-slate-900">{s.name}</p>
                          <span className={urgencyBadge(s.urgency)}>{s.urgency}</span>
                          <span className="text-xs text-slate-400">{s.category}</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-sm mb-3">
                          <div>
                            <span className="text-slate-400">Current:</span>{' '}
                            <span className="font-medium">{s.currentQuantity} {s.unit}</span>
                          </div>
                          <div>
                            <span className="text-slate-400">Par:</span>{' '}
                            <span className="font-medium">{s.parLevel} {s.unit}</span>
                          </div>
                          <div>
                            <span className="text-slate-400">Days left:</span>{' '}
                            <span className="font-medium">
                              {s.daysOfStockRemaining == null ? '—' : `${s.daysOfStockRemaining}d`}
                            </span>
                          </div>
                        </div>

                        {/* Quantity + supplier controls */}
                        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                          {(() => {
                            const chosen = s.suppliers.find((x) => x.vendorId === row.vendorId) || s.suppliers[0];
                            const pack = chosen && Number(chosen.packSize) > 1 ? Number(chosen.packSize) : 1;
                            if (pack > 1) {
                              const units = Math.max(0, Math.ceil((row.quantity || 0) / pack - 1e-9));
                              return (
                                <div>
                                  <label className="block text-xs font-medium text-slate-500 mb-1">
                                    Order Qty ({chosen.orderUnit || 'case'}s — as received &amp; invoiced)
                                  </label>
                                  <input
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={units}
                                    onChange={(e) =>
                                      updateRow(s.inventoryItemId, s, {
                                        quantity: Math.max(0, Math.round(parseFloat(e.target.value) || 0)) * pack,
                                      })
                                    }
                                    className="input w-28"
                                  />
                                  <p className="text-[11px] text-slate-400 mt-1">= {Math.round(units * pack * 100) / 100} {s.unit}</p>
                                </div>
                              );
                            }
                            return (
                              <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">
                                  Order Qty ({s.unit})
                                </label>
                                <input
                                  type="number"
                                  min={0}
                                  value={row.quantity}
                                  onChange={(e) =>
                                    updateRow(s.inventoryItemId, s, {
                                      quantity: parseFloat(e.target.value) || 0,
                                    })
                                  }
                                  className="input w-28"
                                />
                              </div>
                            );
                          })()}
                          <div className="flex-1 min-w-0">
                            <label className="block text-xs font-medium text-slate-500 mb-1">Supplier</label>
                            {s.suppliers.length === 0 ? (
                              vendors.length === 0 ? (
                                <p className="text-sm text-slate-400 py-2">
                                  No suppliers — add one in the Suppliers tab
                                </p>
                              ) : (
                                <select
                                  value={row.vendorId ?? ''}
                                  onChange={(e) =>
                                    updateRow(s.inventoryItemId, s, { vendorId: e.target.value || null })
                                  }
                                  className="input w-full"
                                >
                                  <option value="">Choose a supplier…</option>
                                  {vendors.map((v) => (
                                    <option key={v.id} value={v.id}>{v.name}</option>
                                  ))}
                                </select>
                              )
                            ) : (
                              <select
                                value={row.vendorId ?? ''}
                                onChange={(e) =>
                                  updateRow(s.inventoryItemId, s, { vendorId: e.target.value || null })
                                }
                                className="input w-full"
                              >
                                {s.suppliers.map((sup, idx) => (
                                  <option key={sup.vendorItemId || `${sup.vendorId}-${idx}`} value={sup.vendorId ?? ''}>
                                    {sup.vendorName} — {formatCurrency(sup.unitPriceCents)}/{sup.orderUnit}
                                    {sup.isPreferred ? ' ★' : ''}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        </div>

                        {chosen && (
                          <div className="mt-2 text-sm text-slate-500">
                            {chosen.vendorName}: {formatCurrency(chosen.unitPriceCents)}/{chosen.orderUnit}
                            {' · '}Est. <span className="font-medium text-slate-700">{formatCurrency(chosen.totalCostCents)}</span>
                            {s.recommendedReason && (
                              <span className="text-slate-400"> · {s.recommendedReason}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===================================================================
          TAB: Purchase Orders
      =================================================================== */}
      {activeTab === 'purchase-orders' && (
        <div>
          {sendBanner && (
            <div
              className={clsx(
                'mb-4 flex items-start justify-between gap-3 rounded-xl border px-4 py-3 text-sm',
                sendBanner.tone === 'success'
                  ? 'border-green-200 bg-green-50 text-green-800'
                  : 'border-amber-200 bg-amber-50 text-amber-800'
              )}
            >
              <p>{sendBanner.text}</p>
              <button onClick={() => setSendBanner(null)} aria-label="Dismiss" className="shrink-0 opacity-60 hover:opacity-100">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {loadingPOs ? (
            <div className="text-center py-12">Loading...</div>
          ) : purchaseOrders.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No purchase orders yet</p>
              <p className="text-sm">Create orders from the To Order tab</p>
            </div>
          ) : (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="table-header">PO #</th>
                    <th className="table-header">Vendor</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Items</th>
                    <th className="table-header">Total</th>
                    <th className="table-header">Created</th>
                    <th className="table-header">Expected</th>
                    <th className="table-header"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {purchaseOrders.map((po) => (
                    <tr key={po.id} className="hover:bg-slate-50">
                      <td className="table-cell font-medium">{po.poNumber}</td>
                      <td className="table-cell">{po.vendor?.name || '—'}</td>
                      <td className="table-cell">
                        <span className={statusBadge(po.status)}>{po.status}</span>
                        {po.sentAt && (
                          <span className="ml-2 text-xs text-slate-400">emailed {formatDate(po.sentAt)}</span>
                        )}
                      </td>
                      <td className="table-cell">{po.items?.length || 0}</td>
                      <td className="table-cell">{formatCurrency(po.totalCents)}</td>
                      <td className="table-cell">{formatDate(po.createdAt)}</td>
                      <td className="table-cell">{formatDate(po.expectedDelivery)}</td>
                      <td className="table-cell text-right">
                        {po.status !== 'received' && po.status !== 'cancelled' && (
                          <button
                            onClick={() => sendPOMutation.mutate(po.id)}
                            disabled={sendPOMutation.isPending}
                            className="btn btn-sm btn-secondary inline-flex items-center gap-1.5"
                            title={po.vendor?.email ? `Email this order to ${po.vendor.email}` : 'Email this order to the supplier'}
                          >
                            <Send className="w-3.5 h-3.5" />
                            {sendPOMutation.isPending && sendPOMutation.variables === po.id
                              ? 'Sending…'
                              : po.sentAt ? 'Resend' : 'Send to supplier'}
                          </button>
                        )}
                        {po.vendorId && (po.items?.length || 0) > 0 && (
                          <button
                            onClick={() => { setRepeatDays(7); setRepeatPO(po); }}
                            className="btn btn-sm btn-secondary inline-flex items-center gap-1.5 ml-2"
                            title="Have Mise draft this same order again on a schedule"
                          >
                            <Repeat className="w-3.5 h-3.5" />
                            Repeat
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {recurringOrders.length > 0 && (
            <div className="card mt-6 p-4">
              <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
                <Repeat className="w-4 h-4 text-slate-500" />
                Repeating orders
              </h3>
              <p className="text-sm text-slate-500 mb-3">
                Mise drafts these on schedule — each one appears above as a new purchase order, ready to review and send.
              </p>
              <div className="divide-y">
                {recurringOrders.map((r) => {
                  const vendor = vendors.find((v) => v.id === r.vendorId);
                  const totalCents = r.items.reduce((sum, it) => sum + it.quantity * it.unitCost * 100, 0);
                  return (
                    <div key={r.id} className="flex items-center justify-between gap-3 py-2.5">
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {vendor?.name || 'Supplier'} — {r.items.length} item{r.items.length > 1 ? 's' : ''} · {formatCurrency(totalCents)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {r.frequencyDays % 7 === 0 ? `Every ${r.frequencyDays === 7 ? 'week' : `${r.frequencyDays / 7} weeks`}` : `Every ${r.frequencyDays} days`}
                          {r.active ? ` · next draft ${formatDate(r.nextDate)}` : ' · paused'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => updateRecurrenceMutation.mutate({ id: r.id, data: { active: !r.active } })}
                          className="btn btn-sm btn-secondary"
                        >
                          {r.active ? 'Pause' : 'Resume'}
                        </button>
                        <button
                          onClick={() => deleteRecurrenceMutation.mutate(r.id)}
                          className="btn btn-sm btn-secondary text-slate-500"
                          title="Stop repeating (existing purchase orders stay)"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {repeatPO && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setRepeatPO(null)}>
              <div className="card w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-semibold text-slate-900 mb-1">Repeat this order</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Mise will draft the same order for {repeatPO.vendor?.name || 'this supplier'} on a schedule. You still review and send each one.
                </p>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">How often?</label>
                <select
                  value={repeatDays}
                  onChange={(e) => setRepeatDays(Number(e.target.value))}
                  className="input w-full mb-4"
                >
                  <option value={7}>Every week</option>
                  <option value={14}>Every 2 weeks</option>
                  <option value={28}>Every 4 weeks</option>
                </select>
                <p className="text-xs text-slate-500 mb-4">
                  First draft: {formatDate(new Date(Date.now() + repeatDays * 86400_000).toISOString())}
                </p>
                <div className="flex justify-end gap-2">
                  <button className="btn btn-sm btn-secondary" onClick={() => setRepeatPO(null)}>Cancel</button>
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={createRecurrenceMutation.isPending}
                    onClick={() => createRecurrenceMutation.mutate(repeatPO)}
                  >
                    {createRecurrenceMutation.isPending ? 'Saving…' : 'Start repeating'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===================================================================
          TAB: Suppliers
      =================================================================== */}
      {activeTab === 'suppliers' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {/* Vendor list */}
          <div className="md:col-span-1 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">Suppliers</h2>
              <button
                onClick={() => {
                  setVendorForm(emptyVendorForm);
                  setFormError('');
                  setShowVendorModal(true);
                }}
                className="btn btn-sm btn-secondary flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
            {loadingVendors ? (
              <div className="text-center py-8">Loading...</div>
            ) : vendors.length === 0 ? (
              <div className="card p-6 text-center text-slate-500">
                <Truck className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No suppliers yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {vendors.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVendorId(v.id)}
                    className={clsx(
                      'w-full text-left card p-3 transition-colors hover:bg-slate-50',
                      selectedVendorId === v.id && 'ring-2 ring-primary-400'
                    )}
                  >
                    <p className="font-medium text-slate-900">{v.name}</p>
                    {v.contactName && <p className="text-sm text-slate-500">{v.contactName}</p>}
                    {v.paymentTerms && (
                      <p className="text-xs text-slate-400 mt-1">Terms: {v.paymentTerms}</p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Catalog */}
          <div className="md:col-span-2">
            {!selectedVendorId ? (
              <div className="card p-12 text-center text-slate-500">
                <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Select a supplier to view their catalog</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-slate-900">
                    {vendors.find((v) => v.id === selectedVendorId)?.name || 'Catalog'}
                  </h2>
                  <button onClick={openAddCatalog} className="btn btn-sm btn-primary flex items-center gap-1">
                    <Plus className="w-4 h-4" />
                    Add Item
                  </button>
                </div>

                {loadingCatalog ? (
                  <div className="text-center py-8">Loading...</div>
                ) : catalog.length === 0 ? (
                  <div className="card p-8 text-center text-slate-500">
                    <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No catalog items for this supplier</p>
                  </div>
                ) : (
                  <div className="card overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="table-header">Item</th>
                          <th className="table-header">Pack</th>
                          <th className="table-header">Price / Unit</th>
                          <th className="table-header">Per Base</th>
                          <th className="table-header">Lead</th>
                          <th className="table-header">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {catalog.map((c) => {
                          const perBaseCents =
                            c.packSize > 0 ? c.unitPriceCents / c.packSize : c.unitPriceCents;
                          return (
                            <tr key={c.id} className="hover:bg-slate-50">
                              <td className="table-cell">
                                <div className="flex items-center gap-2">
                                  {c.isPreferred && (
                                    <Star className="w-4 h-4 text-amber-500 fill-amber-500 flex-shrink-0" />
                                  )}
                                  <div>
                                    <p className="font-medium">
                                      {c.inventoryItem?.name || c.vendorItemName}
                                    </p>
                                    {c.vendorSku && (
                                      <p className="text-xs text-slate-400">SKU: {c.vendorSku}</p>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="table-cell text-sm">
                                {c.packSize} {c.packUnit || ''} / {c.orderUnit}
                              </td>
                              <td className="table-cell">
                                {formatCurrency(c.unitPriceCents)}
                                <span className="text-xs text-slate-400">/{c.orderUnit}</span>
                              </td>
                              <td className="table-cell text-sm text-slate-500">
                                {formatCurrency(perBaseCents)}
                              </td>
                              <td className="table-cell text-sm">{c.leadTimeDays}d</td>
                              <td className="table-cell">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => openEditCatalog(c)}
                                    className="btn btn-sm btn-ghost"
                                    title="Edit"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteCatalogMutation.mutate(c.id)}
                                    className="btn btn-sm btn-ghost text-danger-600"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===================================================================
          MODAL: Add Vendor
      =================================================================== */}
      {showVendorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-xl shadow-xl w-full md:max-w-md max-h-[90vh] md:max-h-[85vh] overflow-y-auto p-5 md:p-6 pb-safe">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Add Supplier</h2>
              <button
                onClick={() => {
                  setShowVendorModal(false);
                  setFormError('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {formError}
              </div>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createVendorMutation.mutate(vendorForm);
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                  <input
                    type="text"
                    className="input w-full"
                    value={vendorForm.name}
                    onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Contact</label>
                    <input
                      type="text"
                      className="input w-full"
                      value={vendorForm.contactName}
                      onChange={(e) => setVendorForm({ ...vendorForm, contactName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                    <input
                      type="text"
                      className="input w-full"
                      value={vendorForm.phone}
                      onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    className="input w-full"
                    value={vendorForm.email}
                    onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                  <input
                    type="text"
                    className="input w-full"
                    value={vendorForm.address}
                    onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Terms</label>
                  <input
                    type="text"
                    className="input w-full"
                    placeholder="e.g., Net 30"
                    value={vendorForm.paymentTerms}
                    onChange={(e) => setVendorForm({ ...vendorForm, paymentTerms: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowVendorModal(false);
                    setFormError('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={createVendorMutation.isPending}>
                  {createVendorMutation.isPending ? 'Saving...' : 'Add Supplier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===================================================================
          MODAL: Add / Edit Catalog Item
      =================================================================== */}
      {showCatalogModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-xl shadow-xl w-full md:max-w-md max-h-[90vh] md:max-h-[85vh] overflow-y-auto p-5 md:p-6 pb-safe">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingCatalog ? 'Edit Catalog Item' : 'Add Catalog Item'}</h2>
              <button
                onClick={() => {
                  setShowCatalogModal(false);
                  setEditingCatalog(null);
                  setFormError('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {formError}
              </div>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveCatalogMutation.mutate(catalogForm);
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Inventory Item</label>
                  <select
                    className="input w-full"
                    value={catalogForm.inventoryItemId}
                    onChange={(e) => setCatalogForm({ ...catalogForm, inventoryItemId: e.target.value })}
                  >
                    <option value="">— Not linked —</option>
                    {items.map((it) => (
                      <option key={it.id} value={it.id}>{it.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Vendor Item Name *</label>
                  <input
                    type="text"
                    className="input w-full"
                    value={catalogForm.vendorItemName}
                    onChange={(e) => setCatalogForm({ ...catalogForm, vendorItemName: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Vendor SKU</label>
                    <input
                      type="text"
                      className="input w-full"
                      value={catalogForm.vendorSku}
                      onChange={(e) => setCatalogForm({ ...catalogForm, vendorSku: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Unit Price ($) *</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input w-full"
                      value={catalogForm.unitPrice}
                      onChange={(e) =>
                        setCatalogForm({ ...catalogForm, unitPrice: parseFloat(e.target.value) || 0 })
                      }
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Order Unit *</label>
                    <input
                      type="text"
                      className="input w-full"
                      placeholder="e.g., case"
                      value={catalogForm.orderUnit}
                      onChange={(e) => setCatalogForm({ ...catalogForm, orderUnit: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Pack Unit</label>
                    <input
                      type="text"
                      className="input w-full"
                      placeholder="e.g., lb"
                      value={catalogForm.packUnit}
                      onChange={(e) => setCatalogForm({ ...catalogForm, packUnit: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Pack Size</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input w-full"
                      value={catalogForm.packSize}
                      onChange={(e) =>
                        setCatalogForm({ ...catalogForm, packSize: parseFloat(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Min Order</label>
                    <input
                      type="number"
                      className="input w-full"
                      value={catalogForm.minimumOrder}
                      onChange={(e) =>
                        setCatalogForm({ ...catalogForm, minimumOrder: parseFloat(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Lead (days)</label>
                    <input
                      type="number"
                      className="input w-full"
                      value={catalogForm.leadTimeDays}
                      onChange={(e) =>
                        setCatalogForm({ ...catalogForm, leadTimeDays: parseFloat(e.target.value) || 0 })
                      }
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={catalogForm.isPreferred}
                    onChange={(e) => setCatalogForm({ ...catalogForm, isPreferred: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                  Preferred supplier for this item
                </label>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCatalogModal(false);
                    setEditingCatalog(null);
                    setFormError('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saveCatalogMutation.isPending}>
                  {saveCatalogMutation.isPending ? 'Saving...' : editingCatalog ? 'Save Changes' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
